import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { FluidOwaSource } from 'owa-fluid-validations';
import { getComposeCalendarColorScheme } from 'owa-calendar-compose-form-store/lib/selectors/getComposeCalendarColorScheme';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { Link } from '@fluentui/react/lib/Link';
import generateBorderStyle from 'owa-calendar-forms-common/lib/utils/generateBorderStyle';
import createFluidCollabSpace from '../utils/collabSpaceFluidUtils';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { setIsCollabAllowed } from '../mutators/collabSpaceMutators';
import CollabSpaceRenderer from './CollabSpaceRenderer';
import { collabNotesText } from './CollabSpace.locstring.json';
import {
    cacheSeriesMasterCollabSpace,
    cacheSeriesInstanceCollabSpace,
} from '../utils/collabSpaceCacheUtils';
import loc from 'owa-localize';
import { hasAccessIssue, showErrorInfo } from '../utils/collabSpaceErrorCheck';
import ErrorInfoBar from './ErrorInfoBar';
import { isEventFromDifferentMailbox } from '../utils/isEventFromDifferentMailbox';
import { getViewStateById } from '../selectors/collabSpaceSelectors';
import { collabSpaceContainerId } from '../utils/constants';
import { logUsage } from 'owa-analytics';

interface CollabSpaceComposeProps {
    event: CalendarEvent;
    fluidOwaSource: FluidOwaSource;
    viewStateId: string;
}

const styles = require('./CollabSpaceCompose.scss');
const classNames = require('classnames');

export default observer(function CollabSpaceCompose(props: CollabSpaceComposeProps) {
    const { event, fluidOwaSource, viewStateId } = props;
    const viewState = getViewStateById(viewStateId);
    const { collabAllowed, isNewDoc, isCollabSpacePresent } = viewState;
    const colorScheme: ColorScheme = getComposeCalendarColorScheme();
    const borderColorStyle = generateBorderStyle(colorScheme);
    const showErrorInfoBar = showErrorInfo(viewStateId);

    React.useEffect(() => {
        if (!viewState.seriesMasterShareLink) {
            cacheSeriesMasterCollabSpace(event, viewStateId);
        }

        if (!viewState.seriesInstanceShareLink) {
            cacheSeriesInstanceCollabSpace(event, viewStateId);
        }
    }, [viewState.seriesMasterShareLink, viewState.shareLink]);

    const renderCollabSpace = () => {
        if (!isCollabSpacePresent) {
            createFluidCollabSpace(event, fluidOwaSource, viewStateId);
        } else {
            setIsCollabAllowed(true, viewStateId);
        }

        // WI 111199. Remove isCore from Datapoints after reaching MSIT
        logUsage(
            !isCollabSpacePresent
                ? 'CollabSpace_Toggle_On_Created'
                : 'CollabSpace_Toggle_On_Visible',
            undefined,
            { isCore: true }
        );
    };

    let collabSpaceCreator = null;
    let collabSpaceContent = null;

    if (viewState.meetingsFolderAccessFailed) {
        collabSpaceCreator = <ErrorInfoBar viewStateId={viewStateId} />;
    } else {
        collabSpaceCreator = !(isCollabSpacePresent && collabAllowed) &&
            !isEventFromDifferentMailbox(event) && (
                <>
                    {!hasAccessIssue() && (
                        <div
                            className={classNames(
                                styles.collabContainer,
                                borderColorStyle.bottomBorder
                            )}>
                            <Link className={styles.collabLink} onClick={renderCollabSpace}>
                                {loc(collabNotesText)}
                            </Link>
                        </div>
                    )}
                    {showErrorInfoBar && <ErrorInfoBar viewStateId={viewStateId} />}
                </>
            );

        collabSpaceContent = (
            <>
                <div
                    id={`${collabSpaceContainerId}#${viewStateId}`}
                    className={
                        collabAllowed && isNewDoc
                            ? styles.collabCreateContainerVisible
                            : styles.collabCreateContainerHidden
                    }></div>
                {collabAllowed && !isNewDoc && (
                    <CollabSpaceRenderer
                        event={event}
                        fluidOwaSource={fluidOwaSource}
                        viewStateId={viewStateId}
                    />
                )}
            </>
        );
    }

    return (
        <>
            {collabSpaceCreator}
            {collabSpaceContent}
        </>
    );
});
